import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AdminTicketDetail from "./AdminTicketDetail";
import TicketList from "./TicketList";
import DocGapList from "./DocGapList";
import PromptManager from "./PromptManager";
import AIPerformance from "./AIPerformance";
import KnowledgeBase from "./KnowledgeBase";
import SupportPlayground from "./SupportPlayground";

const LIMIT = 25;
const TABS = ["tickets", "knowledgeBase", "docGaps", "aiPrompt", "playground", "performance"];

export default function AdminSupportTickets() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ticketId } = useParams();
    const [activeTab, setActiveTab] = useState("tickets");
    const [tickets, setTickets] = useState([]);
    const [companies, setCompanies] = useState({});
    const [availableTags, setAvailableTags] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [tagFilter, setTagFilter] = useState("all");
    const [showArchived, setShowArchived] = useState(false);
    const [selectedTickets, setSelectedTickets] = useState(new Set());
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const { call: fetchAdminTickets, isLoading } = useBluelagoon({ method: "getAdminTickets" });
    const { call: fetchFilters } = useBluelagoon({ method: "getAdminTicketFilters" });
    const { call: bulkArchive, isLoading: isArchiving } = useBluelagoon({ method: "bulkArchiveTickets" });

    const fetchTickets = useCallback(async () => {
        const r = await fetchAdminTickets({
            status: statusFilter !== "all" ? statusFilter : undefined,
            priority: priorityFilter !== "all" ? priorityFilter : undefined,
            tag: tagFilter !== "all" ? tagFilter : undefined,
            search: searchQuery || undefined, includeArchived: showArchived || undefined, limit: LIMIT, offset,
        }).catch(() => null);
        if (r?.data?.tickets) { setTickets(r.data.tickets); setTotal(r.data.pagination?.total || r.data.tickets.length); }
    }, [fetchAdminTickets, statusFilter, priorityFilter, tagFilter, searchQuery, showArchived, offset]);

    useEffect(() => {
        fetchFilters().then((r) => {
            if (r?.data?.companies) { const m = {}; r.data.companies.forEach((c) => { m[c.id] = c.name; }); setCompanies(m); }
            if (r?.data?.tags) setAvailableTags(r.data.tags);
        }).catch(() => {});
    }, [fetchFilters]);

    useEffect(() => { if (activeTab === "tickets") fetchTickets(); }, [fetchTickets, activeTab]);
    useEffect(() => { setOffset(0); }, [statusFilter, priorityFilter, tagFilter, searchQuery, showArchived]);

    const toggleSelect = useCallback((id) => {
        setSelectedTickets((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }, []);
    const toggleSelectAll = useCallback(() => {
        setSelectedTickets((p) => p.size === tickets.length ? new Set() : new Set(tickets.map((tk) => tk.id)));
    }, [tickets]);
    const handleBulkArchive = useCallback(async () => {
        if (!selectedTickets.size) return;
        const r = await bulkArchive({ ticketIds: [...selectedTickets], archive: true }).catch(() => null);
        if (r?.status === 200) { toast.success(t("admin.support.bulkArchived", "{{count}} tickets archived", { count: selectedTickets.size })); setSelectedTickets(new Set()); fetchTickets(); }
        else toast.error(t("admin.support.bulkArchiveError", "Failed to archive tickets"));
    }, [selectedTickets, bulkArchive, fetchTickets, t]);

    const ticketIds = tickets.map((tk) => tk.id);
    if (ticketId) return <AdminTicketDetail ticketId={ticketId} onBack={() => navigate("/admin/support")} onNavigate={(id) => navigate(`/admin/support/${id}`)} ticketIds={ticketIds} availableTags={availableTags} />;

    const tabLabels = { tickets: t("admin.support.tickets", "Tickets"), knowledgeBase: t("admin.support.knowledgeBase", "Knowledge Base"), docGaps: t("admin.support.docGaps", "Doc Gaps"), aiPrompt: t("admin.support.aiPrompt", "AI Prompt"), playground: t("admin.support.playground", "Playground"), performance: t("admin.support.performance", "Performance") };
    const filters = { searchQuery, setSearchQuery, statusFilter, setStatusFilter, priorityFilter, setPriorityFilter, tagFilter, setTagFilter, showArchived, setShowArchived, availableTags };

    return (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold">{t("admin.support.title", "Support Tickets")}</h1>
                    <p className="text-sm text-muted-foreground">{t("admin.support.subtitle", "Manage and respond to customer support requests")}</p>
                </div>
                {activeTab === "tickets" && (
                    <Button onClick={fetchTickets} variant="outline" size="sm" className="self-start sm:self-auto">
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />{t("common.refresh", "Refresh")}
                    </Button>
                )}
            </div>
            <div className="flex border-b overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                {TABS.map((tab) => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                        className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {tabLabels[tab]}
                    </button>
                ))}
            </div>
            {activeTab === "tickets" && <TicketList {...{ tickets, isLoading, filters, selectedTickets, toggleSelectAll, toggleSelect, onBulkArchive: handleBulkArchive, isArchiving, onNavigate: (id) => navigate(`/admin/support/${id}`), companies, limit: LIMIT, offset, total, onPageChange: setOffset }} />}
            {activeTab === "knowledgeBase" && <KnowledgeBase />}
            {activeTab === "docGaps" && <DocGapList />}
            {activeTab === "aiPrompt" && <PromptManager />}
            {activeTab === "playground" && <SupportPlayground />}
            {activeTab === "performance" && <AIPerformance />}
        </div>
    );
}
